import { el, escape } from './dom';
import { LivePreviewPanel, type LivePreviewPanel as LivePreviewPanelHandle } from './LivePreviewPanel';
import { batchOutputName, type BatchEntry } from '../lib/batch';
import type { ReviewSelection } from './types';

export type BatchPanelProps = {
  entries: readonly BatchEntry[];
  /** Called when the user hits "redact all & download zip". */
  onConfirmAll: () => void;
  /** Called when the user hits cancel. */
  onCancel: () => void;
};

export type BatchPanelHandle = {
  root: HTMLElement;
  /** Re-render entries on state change (call after dispatch progress, etc). */
  update: (entries: readonly BatchEntry[]) => void;
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const stateLabel = (entry: BatchEntry): string => {
  const s = entry.state;
  if (s.kind === 'pending') return 'queued';
  if (s.kind === 'dispatching') return 'scanning…';
  if (s.kind === 'redacting') return 'redacting…';
  if (s.kind === 'done') return 'done';
  if (s.kind === 'error') return 'error';
  // ready: surface a brief PII count
  const v = s.value;
  if (v.kind === 'text' || v.kind === 'csv') {
    return v.detections.length === 0 ? 'no PII' : `${String(v.detections.length)} PII`;
  }
  if (v.kind === 'image') {
    const parts: string[] = [];
    if (v.exif.fields.length > 0) parts.push(`${String(v.exif.fields.length)} EXIF`);
    if (v.faces.length > 0) parts.push(`${String(v.faces.length)} faces`);
    return parts.length > 0 ? parts.join(' · ') : 'no PII';
  }
  let total = 0;
  for (const p of v.pages) total += p.detections.length;
  if (total === 0 && v.metadata.fields.length === 0) return 'no PII';
  const parts: string[] = [];
  if (total > 0) parts.push(`${String(total)} PII`);
  if (v.metadata.fields.length > 0) parts.push(`${String(v.metadata.fields.length)} meta`);
  return parts.join(' · ');
};

/**
 * Batch review surface. Renders one file at a time using the same
 * LivePreviewPanel the single-file flow uses, with prev/next navigation
 * across the queue and a final "redact all & download zip" CTA.
 *
 * The user reviews each file's detections (toggling, drag-rect manuals,
 * etc.), advances to the next, and at the end fires one zip-and-download
 * for the entire batch. Selections are preserved when navigating back to
 * an already-reviewed file.
 *
 * Files still being dispatched show a "scanning…" placeholder; the user
 * can navigate ahead to files that are ready while later files finish.
 */
export const BatchPanel = (props: BatchPanelProps): BatchPanelHandle => {
  let entries: readonly BatchEntry[] = props.entries;
  let idx = 0;
  let liveHandle: LivePreviewPanelHandle | null = null;
  // Track the last state kind we rendered for the current index. The
  // BatchEntry objects are mutated in place by dispatchAll, so the
  // update() callback re-receives the same array each tick — we can't
  // detect transitions by comparing the array before vs after the
  // assignment. This sidecar field is the only reliable signal that
  // the entry under idx just transitioned.
  let lastRenderedKind: BatchEntry['state']['kind'] | undefined;

  const root = el(`
    <section class="live-preview batch-wizard" id="batch-panel" aria-live="polite">
      <div class="container batch-wizard-shell">
        <header class="batch-wizard-header">
          <div class="batch-wizard-counter mono"></div>
          <div class="batch-wizard-filename"></div>
          <div class="batch-wizard-actions">
            <button type="button" class="batch-wizard-nav" data-action="prev">← previous</button>
            <button type="button" class="batch-wizard-nav" data-action="next">next →</button>
            <button type="button" class="review-cancel" data-action="cancel">cancel</button>
            <button type="button" class="review-confirm" data-action="confirm-all"></button>
          </div>
        </header>
        <div class="batch-wizard-steps"></div>
        <div class="batch-wizard-stage"></div>
      </div>
    </section>
  `);

  const counterEl = root.querySelector<HTMLElement>('.batch-wizard-counter');
  const filenameEl = root.querySelector<HTMLElement>('.batch-wizard-filename');
  const stepsEl = root.querySelector<HTMLElement>('.batch-wizard-steps');
  const stageEl = root.querySelector<HTMLElement>('.batch-wizard-stage');
  const prevBtn = root.querySelector<HTMLButtonElement>('[data-action="prev"]');
  const nextBtn = root.querySelector<HTMLButtonElement>('[data-action="next"]');
  const confirmAllBtn = root.querySelector<HTMLButtonElement>('[data-action="confirm-all"]');

  if (!counterEl || !filenameEl || !stepsEl || !stageEl || !prevBtn || !nextBtn || !confirmAllBtn) {
    throw new Error('BatchPanel: required slots missing');
  }

  // entries[idx] is guarded by goto() so it's always defined at runtime,
  // but TS's noUncheckedIndexedAccess types it as BatchEntry | undefined.
  // This helper centralises the narrowing.
  const currentEntry = (): BatchEntry => {
    const e = entries[idx];
    if (!e) throw new Error(`BatchPanel: idx ${String(idx)} out of bounds`);
    return e;
  };

  // Capture the active LivePreviewPanel's selection (if any) back onto
  // the corresponding BatchEntry before we tear it down.
  const captureSelection = (): void => {
    if (!liveHandle) return;
    const current = currentEntry();
    if (current.state.kind !== 'ready') return;
    current.selection = liveHandle.store.snapshotSelection();
  };

  const renderSteps = (): void => {
    stepsEl.innerHTML = entries
      .map((entry, i) => {
        const isCurrent = i === idx;
        const status =
          entry.state.kind === 'error'
            ? 'error'
            : entry.state.kind === 'ready'
              ? entry.selection
                ? 'reviewed'
                : 'ready'
              : entry.state.kind === 'done'
                ? 'done'
                : 'pending';
        return `
          <button type="button" class="batch-wizard-step ${isCurrent ? 'is-current' : ''}" data-step="${String(i)}" data-status="${status}" title="${escape(entry.file.name)}">
            <span class="batch-wizard-step-num mono">${String(i + 1)}</span>
            <span class="batch-wizard-step-name">${escape(entry.file.name)}</span>
            <span class="batch-wizard-step-state mono">${escape(stateLabel(entry))}</span>
          </button>`;
      })
      .join('');
    stepsEl.querySelectorAll<HTMLButtonElement>('[data-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = Number(btn.dataset.step);
        if (Number.isFinite(target) && target !== idx) goto(target);
      });
    });
  };

  const renderHeader = (): void => {
    const current = currentEntry();
    counterEl.textContent = `file ${String(idx + 1)} of ${String(entries.length)} · ${formatBytes(current.file.size)}`;
    filenameEl.textContent = current.file.name;
    prevBtn.disabled = idx === 0;
    nextBtn.disabled = idx === entries.length - 1;
    const readyCount = entries.filter((e) => e.state.kind === 'ready').length;
    confirmAllBtn.textContent =
      readyCount === entries.length
        ? `redact ${String(entries.length)} & download zip →`
        : `redact ${String(readyCount)} of ${String(entries.length)} when ready →`;
    confirmAllBtn.disabled = readyCount === 0;
  };

  const renderStage = (): void => {
    if (liveHandle) {
      liveHandle.dispose();
      liveHandle.root.remove();
      liveHandle = null;
    }
    stageEl.innerHTML = '';
    const current = currentEntry();
    const s = current.state;
    // Keep the transition tracker in sync with whatever we just
    // rendered, so update() doesn't re-render the stage for the same
    // kind a second time.
    lastRenderedKind = s.kind;
    if (s.kind === 'pending' || s.kind === 'dispatching') {
      stageEl.append(
        el(`<div class="batch-wizard-placeholder mono">scanning ${escape(current.file.name)}…</div>`),
      );
      return;
    }
    if (s.kind === 'error') {
      stageEl.append(
        el(
          `<div class="batch-wizard-placeholder is-error mono">couldn't process ${escape(current.file.name)} — ${escape(s.message)}</div>`,
        ),
      );
      return;
    }
    if (s.kind === 'done') {
      stageEl.append(
        el(`<div class="batch-wizard-placeholder mono">redacted — ${escape(s.outputName)}</div>`),
      );
      return;
    }
    if (s.kind !== 'ready') return;
    const originalExt = current.file.name.split('.').pop() ?? 'bin';
    // The sidebar's filename is cosmetic; the real per-entry output
    // name comes from redactAndZip in batch.ts. Use the file's own
    // MIME so image/HEIC paths get something sensible.
    const outFilename = batchOutputName(idx, current.file.type, originalExt);
    const isLast = idx >= entries.length - 1;
    const handle = LivePreviewPanel({
      value: s.value,
      filename: current.file.name,
      outputFilename: outFilename,
      confirmLabelHtml: isLast
        ? 'redact &amp; download → anomia-redacted.zip'
        : 'save &amp; next →',
      cancelLabel: 'skip',
      onConfirm: (selection: ReviewSelection): void => {
        current.selection = selection;
        renderSteps();
        renderHeader();
        if (isLast) {
          // Last file — fire the same path the header's "redact all"
          // button uses so the user can finish without scrolling up.
          props.onConfirmAll();
        } else {
          goto(idx + 1);
        }
      },
      onCancel: (): void => {
        // In-batch cancel just stashes the current selection and moves
        // on; the wizard's own cancel button handles a real "abort the
        // whole batch" exit.
        captureSelection();
        renderSteps();
        renderHeader();
        if (!isLast) goto(idx + 1);
      },
    });
    // Apply previously-saved selection if the user is returning to this file.
    if (current.selection) {
      const wanted = current.selection;
      for (const e of handle.store.entries) {
        const want =
          (e.kind === 'text' && wanted.textPii.includes(e.detection)) ||
          (e.kind === 'face' && wanted.faces.includes(e.face)) ||
          (e.kind === 'exif' && wanted.exifKeys.includes(e.field.key)) ||
          (e.kind === 'pdf-meta' && wanted.pdfMetaKeys.includes(e.field.key));
        handle.store.setSelected(e.id, want);
      }
      // Re-create user-drawn manual rects. The fresh store has none —
      // they live in `entries` (not in the dispatch value), so we need
      // to replay the addManual calls. Text-highlight labels are lost
      // (the snapshot only captures rects), but the redactions still
      // cover the right pixels.
      for (const [pageIndex, rects] of wanted.manualsByPage.entries()) {
        for (const rect of rects) {
          if (rect.w > 0 && rect.h > 0) handle.store.addManual(pageIndex, rect);
        }
      }
    }
    liveHandle = handle;
    stageEl.append(handle.root);
  };

  const goto = (target: number): void => {
    if (target < 0 || target >= entries.length) return;
    captureSelection();
    idx = target;
    renderHeader();
    renderSteps();
    renderStage();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const update = (next: readonly BatchEntry[]): void => {
    entries = next;
    renderHeader();
    renderSteps();
    // Re-render the stage when the entry under idx transitions to a
    // new state kind ('dispatching' → 'ready', 'ready' → 'redacting',
    // etc). Skip while the kind is unchanged so an in-progress review
    // (kind still 'ready') doesn't get torn down on every progress
    // tick.
    const currentKind = entries[idx]?.state.kind;
    if (currentKind !== lastRenderedKind) {
      lastRenderedKind = currentKind;
      renderStage();
    }
  };

  prevBtn.addEventListener('click', () => {
    goto(idx - 1);
  });
  nextBtn.addEventListener('click', () => {
    goto(idx + 1);
  });
  root.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', () => {
    if (liveHandle) {
      liveHandle.dispose();
      liveHandle.root.remove();
      liveHandle = null;
    }
    props.onCancel();
  });
  confirmAllBtn.addEventListener('click', () => {
    captureSelection();
    props.onConfirmAll();
  });

  renderHeader();
  renderSteps();
  renderStage();
  return { root, update };
};
